import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import React, { useState } from "react";
import { TARGETS, MARKS, targetAction, targetPromise, isPhoneLike } from "./shareTargets";

/**
 * The two pieces of the share experience that don't belong inside createVideo.jsx.
 *
 *  • ProgressCard — one indicator that STAYS until the work is finished. The editor used to
 *    fire a toast ("Trimming clip…") that disappeared after six seconds while the trim carried
 *    on, so people tapped again thinking nothing had happened (Isaac, 2026-09-24).
 *
 *  • ShareBox — the clip, the platforms, and the spreadsheet link, in that order. What a
 *    platform button can actually do differs by device; shareTargets.js owns that decision and
 *    every button says its own promise out loud before it is pressed.
 */

const CARD = {
  bgcolor: "#171320",
  border: "1px solid #3a2b46",
  borderRadius: "14px",
  boxShadow: "0 18px 44px rgba(0,0,0,0.55)",
  color: "#fff",
};

/**
 * Every card here is draggable by its title bar. They land bottom-centre, which is exactly
 * where the clip you are looking at tends to be, so being able to shove one aside matters.
 */
export const DragCard = ({ title, onClose, children, width = 360 }) => {
  const [xy, setXy] = useState(null);
  const grab = React.useRef(null);
  // Esc closes it too.
  React.useEffect(() => {
    if (!onClose) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <Box sx={{
      ...CARD, position: "fixed", zIndex: 12100, maxWidth: "calc(100vw - 24px)",
      width, maxHeight: "84vh", overflowY: "auto",
      ...(xy ? { left: xy.x, top: xy.y, right: "auto", bottom: "auto" }
             : { left: "50%", bottom: 24, transform: "translateX(-50%)" }),
    }}>
      <Box
        onPointerDown={(e) => {
          // A press on the ✕ (or any button in the bar) is a click, not a drag: capturing the
          // pointer here sent its click to the bar instead, so the card could not be closed.
          if (e.target.closest("button, a, input")) return;
          const r = e.currentTarget.parentElement.getBoundingClientRect();
          grab.current = { dx: e.clientX - r.left, dy: e.clientY - r.top, w: r.width };
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          const g = grab.current;
          if (!g) return;
          setXy({
            x: Math.min(Math.max(8, e.clientX - g.dx), window.innerWidth - g.w - 8),
            y: Math.min(Math.max(8, e.clientY - g.dy), window.innerHeight - 70),
          });
        }}
        onPointerUp={() => { grab.current = null; }}
        sx={{
          display: "flex", alignItems: "center", gap: 1, p: 1.8, cursor: "grab",
          borderBottom: "1px solid #2b2335", touchAction: "none", userSelect: "none",
          "&:active": { cursor: "grabbing" },
        }}
      >
        <Typography sx={{ fontSize: 16, fontWeight: 800, flex: 1 }}>{title}</Typography>
        <Typography sx={{ fontSize: 15, color: "#6b6475" }} title="Drag me">⠿</Typography>
        <Button onClick={onClose} sx={{ minWidth: "unset", p: 0.4, color: "#8b8592", fontSize: 15, "&:hover": { color: "#fff" } }} aria-label="Close">✕</Button>
      </Box>
      {children}
    </Box>
  );
};

const BAND = {
  fontSize: 11, fontWeight: 800, letterSpacing: ".12em",
  textTransform: "uppercase", color: "#E61D50", mb: 1.2,
};

/** The iPhone share glyph — the button people already tap to share on a phone. */
export const ShareIcon = ({ size = 17 }) => (
  <Box component="svg" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"
    sx={{ display: "block", flex: "none" }} aria-hidden="true">
    <path d="M12 3v12" />
    <path d="M8.5 6.5 12 3l3.5 3.5" />
    <path d="M7.5 10H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-1.5" />
  </Box>
);

/**
 * A determinate-looking ring that does NOT claim to know the percentage — the server gives no
 * progress for a trim, so it creeps toward 90% and only completes when the file really lands.
 * An honest "still working" beats a bar that hits 100% and then sits there.
 */
export const ProgressCard = ({ stage = "Cutting your clip…", sub = "", onCancel = null }) => {
  const [pct, setPct] = useState(6);
  React.useEffect(() => {
    const t = setInterval(() => setPct((p) => (p >= 90 ? 90 : p + Math.max(1, (90 - p) / 14))), 240);
    return () => clearInterval(t);
  }, []);
  const R = 30;
  const C = 2 * Math.PI * R;
  return (
    <Box sx={{
      ...CARD, position: "fixed", left: 12, right: 12, bottom: 24, mx: "auto",
      maxWidth: 290, zIndex: 12100, p: 2, display: "flex", flexDirection: "column",
      alignItems: "center", gap: 1.2,
    }}>
      <Box component="svg" width={62} height={62} viewBox="0 0 70 70" aria-hidden="true">
        <circle cx="35" cy="35" r={R} fill="none" stroke="#2b2335" strokeWidth="6" />
        <circle cx="35" cy="35" r={R} fill="none" stroke="#E61D50" strokeWidth="6"
          strokeLinecap="round" strokeDasharray={C} strokeDashoffset={C * (1 - pct / 100)}
          transform="rotate(-90 35 35)" style={{ transition: "stroke-dashoffset .4s ease" }} />
      </Box>
      <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{stage}</Typography>
      {sub && (
        <Typography sx={{ fontSize: 11, color: "#7d7488", textAlign: "center", lineHeight: 1.45 }}>
          {sub}
        </Typography>
      )}
      {onCancel && (
        <Button onClick={onCancel} sx={{
          textTransform: "none", fontSize: 11.5, color: "#8b8592", minWidth: "unset", p: 0.3,
          "&:hover": { color: "#fff" },
        }}>
          Cancel
        </Button>
      )}
    </Box>
  );
};

/**
 * The share box.
 *
 * On a PHONE every platform button works, because it goes through the system sheet and the
 * video really is attached. On a LAPTOP no browser can put a file into another site's
 * composer, so the platform row is not shown at all — a button that saves a file and opens a
 * logged-out Facebook page is worse than no button (Isaac, 2026-09-24).
 */
export const ShareBox = ({
  clip, canShareFile, sheetUrl, busy = false,
  onTarget, onShare, onSave, onSheetLink, onClose,
}) => {
  const [confirm, setConfirm] = useState(null);
  const [caption, setCaption] = useState(clip?.title || "");

  const logo = (t, size = 22) => (
    <Box component="svg" width={size} height={size} viewBox="0 0 24 24"
      sx={{ display: "block" }} aria-hidden="true">
      <path d={MARKS[t.key]} fill="currentColor" />
    </Box>
  );

  const dot = (t) => {
    const off = targetAction(t, canShareFile) === "unavailable";
    return (
      <Box key={t.key} component="button" type="button" disabled={off}
        onClick={() => setConfirm(t)}
        title={targetPromise(t, canShareFile)}
        sx={{
          width: 52, border: "none", background: "none", p: 0,
          cursor: off ? "not-allowed" : "pointer", font: "inherit", textAlign: "center",
        }}>
        <Box sx={{
          width: 50, height: 50, borderRadius: "14px", display: "grid", placeItems: "center",
          color: "#fff", background: t.color, mb: 0.5,
          border: t.key === "x" || t.key === "tiktok" ? "1px solid #38343d" : "none",
          filter: off ? "grayscale(1)" : "none", opacity: off ? 0.38 : 1,
          transition: "transform .15s",
          "&:hover": off ? {} : { transform: "translateY(-2px) scale(1.06)" },
        }}>
          {logo(t)}
        </Box>
        <Typography sx={{ fontSize: 9.5, color: off ? "#6b6475" : "#9d94a8" }}>
          {off ? "phone only" : t.name}
        </Typography>
      </Box>
    );
  };

  // ── confirm: what will post, before anything is cut ─────────────────────────
  if (confirm) {
    return (
      <DragCard title={`Post to ${confirm.name}`} onClose={onClose} width={360}>
        <Box sx={{ p: 1.8 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.5 }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: "12px", display: "grid", placeItems: "center",
              background: confirm.color, color: "#fff", flex: "none",
            }}>{logo(confirm, 24)}</Box>
            <Typography sx={{ fontSize: 12.5, color: "#c9b8d6", lineHeight: 1.45 }}>
              {targetPromise(confirm, canShareFile)}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 11, color: "#7d7488", mb: 0.6 }}>Your caption</Typography>
          <Box component="textarea" rows={3} value={caption}
            onChange={(e) => setCaption(e.target.value)} aria-label="Caption"
            sx={{
              width: "100%", bgcolor: "#100d17", border: "1px solid #2b2335", borderRadius: "8px",
              color: "#e8e2ee", font: "inherit", fontSize: 13.5, p: 1.1, resize: "none", mb: 1.6,
            }} />
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={() => setConfirm(null)} sx={{
              flex: 1, textTransform: "none", fontWeight: 700, fontSize: 13.5, borderRadius: "9px",
              py: 1, color: "#c9b8d6", border: "1px solid #3a2b46",
            }}>Back</Button>
            <Button disabled={busy} onClick={() => onTarget(confirm.key, caption)} sx={{
              flex: 1.5, textTransform: "none", fontWeight: 700, fontSize: 13.5, borderRadius: "9px",
              py: 1, color: "#fff", bgcolor: confirm.color, "&:hover": { filter: "brightness(1.1)" },
              "&.Mui-disabled": { bgcolor: "#2a1a22", color: "#6b6475" },
            }}>Send to {confirm.name}</Button>
          </Box>
          <Typography sx={{ fontSize: 11, color: "#7d7488", mt: 1.2 }}>
            Nothing is cut until you press this.
          </Typography>
        </Box>
      </DragCard>
    );
  }

  // ── the box ────────────────────────────────────────────────────────────────
  return (
    <DragCard title="Share" onClose={onClose} width={370}>
      <Box sx={{ p: 1.8 }}>
        <Typography sx={BAND}>Post this clip</Typography>
        {clip?.title && (
          <Box sx={{ mb: 1.6 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.35 }}>
              &ldquo;{clip.title}&rdquo;
            </Typography>
            {clip.timecode && (
              <Typography sx={{ fontFamily: "monospace", fontSize: 11, color: "#8a8793", mt: 0.5 }}>
                {clip.timecode}
              </Typography>
            )}
          </Box>
        )}

        {true ? (
          <>
            <Box sx={{ display: "flex", gap: 1.2, flexWrap: "wrap", mb: 1.8 }}>
              {TARGETS.map(dot)}
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button onClick={() => onShare(caption)} disabled={busy} sx={{
                flex: 1, textTransform: "none", fontWeight: 700, fontSize: 13.5, borderRadius: "9px",
                py: 1, color: "#fff", bgcolor: "#E61D50", gap: 0.8, "&:hover": { bgcolor: "#c81642" },
              }}>
                <ShareIcon size={16} /> {isPhoneLike() ? "More\u2026" : "Share video"}
              </Button>
              <Button onClick={onSave} disabled={busy} sx={{
                flex: 1, textTransform: "none", fontWeight: 700, fontSize: 13.5, borderRadius: "9px",
                py: 1, color: "#c9b8d6", border: "1px solid #3a2b46",
              }}>
                &#11015; Save
              </Button>
            </Box>
            {!isPhoneLike() && (
              <Typography sx={{ fontSize: 12, color: "#9d94a8", mt: 1.4, lineHeight: 1.5 }}>
                <b style={{ color: "#e8e2ee" }}>Greyed out means that app only accepts uploads from
                inside itself.</b>{" "} The rest open in a tab and take your words — the video
                saves to your Downloads and you attach it. On a phone all six take the clip directly.
              </Typography>
            )}
          </>
        ) : (
          <>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button onClick={() => onShare(caption)} disabled={busy} sx={{
                flex: 1, textTransform: "none", fontWeight: 700, fontSize: 13.5, borderRadius: "9px",
                py: 1, color: "#fff", bgcolor: "#E61D50", gap: 0.8, "&:hover": { bgcolor: "#c81642" },
              }}>
                <ShareIcon size={16} /> Share video
              </Button>
              <Button onClick={onSave} disabled={busy} sx={{
                flex: 1, textTransform: "none", fontWeight: 700, fontSize: 13.5, borderRadius: "9px",
                py: 1, color: "#c9b8d6", border: "1px solid #3a2b46",
              }}>
                &#11015; Save
              </Button>
            </Box>
            <Typography sx={{ fontSize: 12, color: "#9d94a8", mt: 1.4, lineHeight: 1.5 }}>
              <b style={{ color: "#e8e2ee" }}>WhatsApp, Instagram and the rest live on your phone.</b>{" "}
              A laptop&rsquo;s share sheet only has AirDrop, Mail and Notes in it &mdash; no social apps
              &mdash; and no browser can put a video into another website. Open this page on your
              phone and all six appear here, with the clip attached.
            </Typography>
          </>
        )}
      </Box>

      <Box sx={{ height: 6, bgcolor: "#0d0a13" }} />

      <Box sx={{ p: 1.8 }}>
        <Typography sx={BAND}>Or send the whole spreadsheet</Typography>
        <Typography sx={{ fontSize: 12.5, color: "#9d94a8", lineHeight: 1.5, mb: 1.4 }}>
          So your friend or your team can make their own cherrypicks. No sign-in.
        </Typography>
        <Box sx={{
          fontFamily: "monospace", fontSize: 11.5, color: "#c9b8d6", mb: 1.2, p: 1.1,
          borderRadius: "8px", bgcolor: "#100d17", border: "1px solid #2b2335", wordBreak: "break-all",
        }}>
          {sheetUrl || "Press Copy link to make one"}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={() => onSheetLink("copy")} sx={{
            flex: 1, textTransform: "none", fontWeight: 700, fontSize: 13.5, borderRadius: "9px",
            py: 1, color: "#7fd3ff", border: "1px solid rgba(41,182,246,.45)",
          }}>
            &#128279; Copy link
          </Button>
          <Button onClick={() => onSheetLink("share")} sx={{
            flex: 1, textTransform: "none", fontWeight: 700, fontSize: 13.5, borderRadius: "9px",
            py: 1, color: "#c9a4f8", border: "1px solid rgba(168,85,247,.45)", gap: 0.7,
          }}>
            <ShareIcon size={15} /> Send
          </Button>
        </Box>
      </Box>
    </DragCard>
  );
};
